import React from 'react';
import { SalesLayout } from '@/components/SalesLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, Edit2, Trash2, Eye, Printer, FileText, 
  ChevronDown, ChevronUp, X, Upload, Image
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
};

// Page size dimensions in mm
const PAGE_SIZES = {
  A4: { width: 210, height: 297, label: 'A4 (210 x 297 mm)' },
  A5: { width: 148, height: 210, label: 'A5 (148 x 210 mm)' }
};

// Placeholder chip component
const PlaceholderChip = ({ field, onClick }) => (
  <button
    type="button"
    onClick={() => onClick(field.key)}
    className="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 transition-colors m-0.5"
    title={`Insert {{${field.key}}}`}
  >
    {field.label}
  </button>
);

// Placeholder category component
const PlaceholderCategory = ({ category, fields, onInsert, isExpanded, onToggle }) => (
  <div className="border rounded-lg mb-2 overflow-hidden">
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between p-2 bg-gray-50 hover:bg-gray-100 transition-colors"
    >
      <span className="font-medium text-sm text-gray-700">{category}</span>
      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
    </button>
    {isExpanded && (
      <div className="p-2 flex flex-wrap">
        {fields.map(field => (
          <PlaceholderChip key={field.key} field={field} onClick={onInsert} />
        ))}
      </div>
    )}
  </div>
);

function ReportTemplates({ user, onLogout }) {
  const [templates, setTemplates] = React.useState([]);
  const [placeholders, setPlaceholders] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [showModal, setShowModal] = React.useState(false);
  const [editingTemplate, setEditingTemplate] = React.useState(null);
  const [expandedCategories, setExpandedCategories] = React.useState({});
  const [activeEditorField, setActiveEditorField] = React.useState('body'); // 'header', 'body', 'footer'
  
  // Preview state
  const [showPreview, setShowPreview] = React.useState(false);
  const [previewContent, setPreviewContent] = React.useState(null);
  const [bookings, setBookings] = React.useState([]);
  const [selectedBookingId, setSelectedBookingId] = React.useState('');
  
  // Form state
  const [formData, setFormData] = React.useState({
    name: '',
    description: '',
    page_size: 'A4',
    margins: { top: 20, bottom: 20, left: 15, right: 15 },
    header_content: '',
    footer_content: '',
    body_content: '',
    logo_base64: null
  });

  React.useEffect(() => {
    fetchTemplates();
    fetchPlaceholders();
    fetchBookings();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/report-templates`, getAuthHeader());
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to fetch templates');
    }
    setLoading(false);
  };

  const fetchPlaceholders = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/report-templates/placeholders`, getAuthHeader());
      setPlaceholders(response.data);
      // Expand first category by default
      const firstKey = Object.keys(response.data)[0];
      if (firstKey) {
        setExpandedCategories({ [firstKey]: true });
      }
    } catch (error) {
      console.error('Failed to fetch placeholders');
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/bookings?limit=100`, getAuthHeader());
      setBookings(response.data);
    } catch (error) {
      console.error('Failed to fetch bookings');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleMarginChange = (side, value) => {
    setFormData(prev => ({
      ...prev,
      margins: { ...prev.margins, [side]: parseFloat(value) || 0 }
    }));
  };

  const insertPlaceholder = (key) => {
    const placeholder = `{{${key}}}`;
    const fieldMap = {
      header: 'header_content',
      body: 'body_content',
      footer: 'footer_content'
    };
    const field = fieldMap[activeEditorField];
    setFormData(prev => ({
      ...prev,
      [field]: prev[field] + placeholder
    }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 500000) {
        toast.error('Logo must be less than 500KB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, logo_base64: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setFormData(prev => ({ ...prev, logo_base64: null }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      page_size: 'A4',
      margins: { top: 20, bottom: 20, left: 15, right: 15 },
      header_content: '',
      footer_content: '',
      body_content: '',
      logo_base64: null
    });
    setEditingTemplate(null);
    setActiveEditorField('body');
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      page_size: template.page_size || 'A4',
      margins: template.margins || { top: 20, bottom: 20, left: 15, right: 15 },
      header_content: template.header_content || '',
      footer_content: template.footer_content || '',
      body_content: template.body_content || '',
      logo_base64: template.logo_base64 || null
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Template name is required');
      return;
    }
    
    try {
      if (editingTemplate) {
        await axios.put(
          `${BACKEND_URL}/api/report-templates/${editingTemplate.id}`,
          formData,
          getAuthHeader()
        );
        toast.success('Template updated successfully');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/report-templates`,
          formData,
          getAuthHeader()
        );
        toast.success('Template created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/report-templates/${templateId}`, getAuthHeader());
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handlePreview = async (template) => {
    if (!selectedBookingId) {
      toast.error('Please select a booking to preview');
      return;
    }
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/report-templates/generate-preview`,
        { template_id: template.id, booking_id: selectedBookingId },
        getAuthHeader()
      );
      setPreviewContent(response.data);
      setShowPreview(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate preview');
    }
  };

  const handlePrint = () => {
    if (!previewContent) return;
    
    const printWindow = window.open('', '_blank');
    const pageSize = PAGE_SIZES[previewContent.page_size || 'A4'];
    const margins = previewContent.margins || { top: 20, bottom: 20, left: 15, right: 15 };
    
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${previewContent.template_name} - ${previewContent.customer_name}</title>
        <style>
          @page {
            size: ${previewContent.page_size || 'A4'};
            margin: ${margins.top}mm ${margins.right}mm ${margins.bottom}mm ${margins.left}mm;
          }
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { 
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            font-size: 12pt;
            line-height: 1.5;
            color: #333;
          }
          .page-container {
            width: 100%;
            min-height: 100vh;
            display: flex;
            flex-direction: column;
          }
          .header {
            border-bottom: 2px solid #333;
            padding-bottom: 10px;
            margin-bottom: 15px;
          }
          .header-content {
            display: flex;
            align-items: center;
            gap: 15px;
          }
          .logo {
            max-height: 60px;
            max-width: 150px;
            object-fit: contain;
          }
          .body-content {
            flex: 1;
          }
          .footer {
            border-top: 1px solid #999;
            padding-top: 10px;
            margin-top: auto;
            font-size: 10pt;
            color: #666;
          }
          table { width: 100%; border-collapse: collapse; margin: 10px 0; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f5f5f5; font-weight: 600; }
          h1 { font-size: 18pt; margin-bottom: 10px; }
          h2 { font-size: 14pt; margin-bottom: 8px; }
          p { margin-bottom: 8px; }
          @media print {
            body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div class="page-container">
          ${previewContent.header_content ? `
            <div class="header">
              <div class="header-content">
                ${previewContent.logo_base64 ? `<img src="${previewContent.logo_base64}" class="logo" />` : ''}
                <div>${previewContent.header_content}</div>
              </div>
            </div>
          ` : ''}
          
          <div class="body-content">
            ${previewContent.body_content}
          </div>
          
          ${previewContent.footer_content ? `
            <div class="footer">
              ${previewContent.footer_content}
            </div>
          ` : ''}
        </div>
        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const toggleCategory = (key) => {
    setExpandedCategories(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return (
      <SalesLayout user={user} onLogout={onLogout} title="Report Templates">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F4ACB7]"></div>
        </div>
      </SalesLayout>
    );
  }

  return (
    <SalesLayout user={user} onLogout={onLogout} title="Report Templates">
      <div className="max-w-7xl mx-auto p-4 space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl border shadow-sm p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <FileText className="w-7 h-7 text-[#F4ACB7]" />
                Report Templates
              </h1>
              <p className="text-gray-500 mt-1">Create printable document templates with dynamic placeholders</p>
            </div>
            <button
              onClick={openCreateModal}
              className="flex items-center gap-2 px-4 py-2.5 text-white rounded-xl shadow-md hover:opacity-90 transition-all"
              style={{ background: 'linear-gradient(135deg, #F4ACB7 0%, #E88D9B 100%)' }}
            >
              <Plus className="w-5 h-5" />
              Create Template
            </button>
          </div>
        </div>

        {/* Booking Selector for Preview */}
        <div className="bg-white rounded-2xl border shadow-sm p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Booking for Preview
          </label>
          <select
            value={selectedBookingId}
            onChange={(e) => setSelectedBookingId(e.target.value)}
            className="w-full max-w-md p-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#F4ACB7] focus:border-transparent"
          >
            <option value="">-- Select a Booking --</option>
            {bookings.map(b => (
              <option key={b.id} value={b.id}>
                #{b.booking_no} - {b.customer_first_name} {b.customer_last_name} ({b.model})
              </option>
            ))}
          </select>
        </div>

        {/* Templates List */}
        {templates.length === 0 ? (
          <div className="bg-white rounded-2xl border shadow-sm p-12 text-center">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-700">No Templates Yet</h3>
            <p className="text-gray-500 mt-1">Create your first report template to get started</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map(template => (
              <div 
                key={template.id} 
                className="bg-white rounded-2xl border shadow-sm overflow-hidden hover:shadow-md transition-shadow"
              >
                <div className="p-4 border-b bg-gradient-to-r from-gray-50 to-white">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-800">{template.name}</h3>
                      <p className="text-sm text-gray-500 mt-1">{template.description || 'No description'}</p>
                    </div>
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                      {template.page_size}
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <div className="text-xs text-gray-400 mb-3">
                    Created: {new Date(template.created_at).toLocaleDateString()}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePreview(template)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-green-50 text-green-700 rounded-lg text-sm font-medium hover:bg-green-100 transition-colors"
                      disabled={!selectedBookingId}
                      title={!selectedBookingId ? 'Select a booking first' : 'Preview'}
                    >
                      <Eye className="w-4 h-4" />
                      Preview
                    </button>
                    <button
                      onClick={() => openEditModal(template)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(template.id)}
                      className="px-3 py-2 bg-red-50 text-red-700 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl mx-4 my-4">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-[#F4ACB7] to-[#E88D9B] text-white rounded-t-2xl">
                <h2 className="text-lg font-bold">
                  {editingTemplate ? 'Edit Template' : 'Create New Template'}
                </h2>
                <button
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="p-4 grid grid-cols-1 lg:grid-cols-3 gap-4 max-h-[70vh] overflow-y-auto">
                  {/* Left Column - Settings */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Template Name *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        className="w-full p-2.5 border rounded-xl focus:ring-2 focus:ring-[#F4ACB7]"
                        placeholder="e.g., Booking Confirmation"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        className="w-full p-2.5 border rounded-xl focus:ring-2 focus:ring-[#F4ACB7]"
                        rows={2}
                        placeholder="Brief description..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Page Size</label>
                      <select
                        value={formData.page_size}
                        onChange={(e) => handleInputChange('page_size', e.target.value)}
                        className="w-full p-2.5 border rounded-xl focus:ring-2 focus:ring-[#F4ACB7]"
                      >
                        {Object.entries(PAGE_SIZES).map(([key, val]) => (
                          <option key={key} value={key}>{val.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Margins (mm)</label>
                      <div className="grid grid-cols-2 gap-2">
                        {['top', 'bottom', 'left', 'right'].map(side => (
                          <div key={side}>
                            <label className="text-xs text-gray-500 capitalize">{side}</label>
                            <input
                              type="number"
                              value={formData.margins[side]}
                              onChange={(e) => handleMarginChange(side, e.target.value)}
                              className="w-full p-2 border rounded-lg text-sm"
                              min="0"
                              max="50"
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Logo (Optional)</label>
                      {formData.logo_base64 ? (
                        <div className="relative inline-block">
                          <img 
                            src={formData.logo_base64} 
                            alt="Logo" 
                            className="h-16 object-contain border rounded-lg p-1"
                          />
                          <button
                            type="button"
                            onClick={removeLogo}
                            className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-xl cursor-pointer hover:bg-gray-50">
                          <Upload className="w-5 h-5 text-gray-400" />
                          <span className="text-sm text-gray-500">Upload Logo</span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleLogoUpload}
                            className="hidden"
                          />
                        </label>
                      )}
                      <p className="text-xs text-gray-400 mt-1">Max 500KB, PNG/JPG recommended</p>
                    </div>

                    {/* Placeholders */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Insert Placeholders
                      </label>
                      <p className="text-xs text-gray-500 mb-2">Click to insert into active editor</p>
                      <div className="max-h-60 overflow-y-auto border rounded-xl p-2">
                        {Object.entries(placeholders).map(([key, category]) => (
                          <PlaceholderCategory
                            key={key}
                            category={category.label}
                            fields={category.fields}
                            onInsert={insertPlaceholder}
                            isExpanded={expandedCategories[key]}
                            onToggle={() => toggleCategory(key)}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Content Editors */}
                  <div className="lg:col-span-2 space-y-4">
                    {/* Editor Tabs */}
                    <div className="flex gap-2 border-b pb-2">
                      {[
                        { key: 'header', label: 'Header' },
                        { key: 'body', label: 'Body Content' },
                        { key: 'footer', label: 'Footer' }
                      ].map(tab => (
                        <button
                          key={tab.key}
                          type="button"
                          onClick={() => setActiveEditorField(tab.key)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            activeEditorField === tab.key
                              ? 'bg-[#F4ACB7] text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>

                    {/* Active Editor */}
                    <div>
                      {activeEditorField === 'header' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Header Content (appears at top of each page)
                          </label>
                          <textarea
                            value={formData.header_content}
                            onChange={(e) => handleInputChange('header_content', e.target.value)}
                            className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-[#F4ACB7] font-mono text-sm"
                            rows={6}
                            placeholder="<h1>{{company_name}}</h1>&#10;<p>{{company_address}}</p>&#10;<p>Tel: {{company_mobile}} | Email: {{company_email}}</p>"
                          />
                        </div>
                      )}

                      {activeEditorField === 'body' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Body Content (main document content)
                          </label>
                          <textarea
                            value={formData.body_content}
                            onChange={(e) => handleInputChange('body_content', e.target.value)}
                            className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-[#F4ACB7] font-mono text-sm"
                            rows={15}
                            placeholder="<h2>BOOKING CONFIRMATION</h2>&#10;&#10;<p>Date: {{current_date}}</p>&#10;<p>Booking No: {{booking_no}}</p>&#10;&#10;<h3>Customer Details</h3>&#10;<p>Name: {{salutation}} {{customer_first_name}} {{customer_last_name}}</p>&#10;<p>Mobile: {{mobile_no_1}}</p>&#10;<p>Address: {{address_1}}, {{city}} - {{pin_code}}</p>&#10;&#10;<h3>Vehicle Details</h3>&#10;<table>&#10;  <tr><th>Brand</th><td>{{brand_name}}</td></tr>&#10;  <tr><th>Model</th><td>{{model}}</td></tr>&#10;  <tr><th>Variant</th><td>{{variant}}</td></tr>&#10;  <tr><th>Color</th><td>{{color}}</td></tr>&#10;  <tr><th>Chassis No</th><td>{{chassis_no}}</td></tr>&#10;</table>"
                          />
                        </div>
                      )}

                      {activeEditorField === 'footer' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Footer Content (appears at bottom of each page)
                          </label>
                          <textarea
                            value={formData.footer_content}
                            onChange={(e) => handleInputChange('footer_content', e.target.value)}
                            className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-[#F4ACB7] font-mono text-sm"
                            rows={6}
                            placeholder="<p>This is a computer-generated document. No signature required.</p>&#10;<p>Page 1 of 1 | Generated on {{current_date}} at {{current_time}}</p>"
                          />
                        </div>
                      )}

                      <p className="text-xs text-gray-500 mt-2">
                        Supports HTML: &lt;h1&gt;, &lt;h2&gt;, &lt;p&gt;, &lt;table&gt;, &lt;tr&gt;, &lt;th&gt;, &lt;td&gt;, &lt;strong&gt;, &lt;em&gt;, etc.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="flex justify-end gap-3 p-4 border-t bg-gray-50 rounded-b-2xl">
                  <button
                    type="button"
                    onClick={() => { setShowModal(false); resetForm(); }}
                    className="px-4 py-2 border border-gray-300 rounded-xl hover:bg-gray-100 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 text-white rounded-xl shadow-md hover:opacity-90 transition-all"
                    style={{ background: 'linear-gradient(135deg, #F4ACB7 0%, #E88D9B 100%)' }}
                  >
                    {editingTemplate ? 'Update Template' : 'Create Template'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Preview Modal */}
        {showPreview && previewContent && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
              {/* Preview Header */}
              <div className="flex items-center justify-between p-4 border-b">
                <div>
                  <h2 className="text-lg font-bold text-gray-800">
                    Preview: {previewContent.template_name}
                  </h2>
                  <p className="text-sm text-gray-500">
                    Booking #{previewContent.booking_no} - {previewContent.customer_name}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handlePrint}
                    className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <Printer className="w-4 h-4" />
                    Print
                  </button>
                  <button
                    onClick={() => setShowPreview(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Preview Content */}
              <div className="flex-1 overflow-y-auto p-6 bg-gray-100">
                <div 
                  className="bg-white mx-auto shadow-lg"
                  style={{
                    width: previewContent.page_size === 'A5' ? '370px' : '595px',
                    minHeight: previewContent.page_size === 'A5' ? '525px' : '842px',
                    padding: `${previewContent.margins?.top || 20}px ${previewContent.margins?.right || 15}px ${previewContent.margins?.bottom || 20}px ${previewContent.margins?.left || 15}px`
                  }}
                >
                  {/* Header */}
                  {previewContent.header_content && (
                    <div className="border-b-2 border-gray-800 pb-3 mb-4">
                      <div className="flex items-center gap-4">
                        {previewContent.logo_base64 && (
                          <img 
                            src={previewContent.logo_base64} 
                            alt="Logo" 
                            className="h-12 object-contain"
                          />
                        )}
                        <div 
                          className="preview-html"
                          dangerouslySetInnerHTML={{ __html: previewContent.header_content }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Body */}
                  <div 
                    className="preview-html flex-1"
                    dangerouslySetInnerHTML={{ __html: previewContent.body_content }}
                  />

                  {/* Footer */}
                  {previewContent.footer_content && (
                    <div className="border-t border-gray-400 pt-3 mt-4 text-sm text-gray-600">
                      <div 
                        className="preview-html"
                        dangerouslySetInnerHTML={{ __html: previewContent.footer_content }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Preview HTML Styles */}
        <style>{`
          .preview-html h1 { font-size: 18px; font-weight: bold; margin-bottom: 8px; }
          .preview-html h2 { font-size: 16px; font-weight: bold; margin-bottom: 6px; margin-top: 12px; }
          .preview-html h3 { font-size: 14px; font-weight: bold; margin-bottom: 4px; margin-top: 10px; }
          .preview-html p { margin-bottom: 6px; font-size: 12px; }
          .preview-html table { width: 100%; border-collapse: collapse; margin: 8px 0; font-size: 11px; }
          .preview-html th, .preview-html td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
          .preview-html th { background-color: #f5f5f5; font-weight: 600; }
          .preview-html strong { font-weight: bold; }
          .preview-html em { font-style: italic; }
          .preview-html ul, .preview-html ol { margin-left: 20px; margin-bottom: 8px; }
          .preview-html li { margin-bottom: 4px; font-size: 12px; }
        `}</style>
      </div>
    </SalesLayout>
  );
}

export default ReportTemplates;
